import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, combineLatest, merge } from 'rxjs';
import { FinancialInstitution, IntegrationEventData, IntegrationEventType } from './bank-account.model';
import { FeatureFlagService, MessageService } from 'common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AppSettings } from '../../../src/app.settings';
import { PlaidService } from './plaid.service';
import { DecisionLogicService } from './decision-logic.service';
import { FinicityService } from './finicity.service';
import { BankAccountLinkFailedDialogComponent } from './bank-account-link-failed-dialog/bank-account-link-failed-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { BankAccountLinkSuccessDialogComponent } from './bank-account-link-success-dialog/bank-account-link-success-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class BankingService {

  onInstitutionLinkStatusChanged: ReplaySubject<boolean>;
  obsInstitutionLinkStatus: Observable<boolean>;

  onSyncStarted: ReplaySubject<any>;
  obsSync: Observable<any>;

  private debug = true;
  private readonly baseUrl: string;
  private integrationStack: Function[];

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private router: Router,
    private settings: AppSettings,
    private plaidService: PlaidService,
    private finicityService: FinicityService,
    private decisionLogicService: DecisionLogicService,
    private featureFlagService: FeatureFlagService,
    private messageService: MessageService
  ) {

    this.baseUrl = `${this.settings.banking.url}`;
    this.onInstitutionLinkStatusChanged = new ReplaySubject<any>(1);
    this.obsInstitutionLinkStatus = this.onInstitutionLinkStatusChanged.asObservable();

    this.onSyncStarted = new ReplaySubject<any>(1);
    this.obsSync = this.onSyncStarted.asObservable();
    this.integrationStack = [];

    merge(
      this.plaidService.obsIntegrationEvent,
      this.decisionLogicService.obsIntegrationEvent,
      this.finicityService.obsIntegrationEvent
    )
      .subscribe(
        e => {
          if (e.type == IntegrationEventType.Pending || e.type == IntegrationEventType.Success)
            this.success(e);
          else if (e.type == IntegrationEventType.Error || e.type == IntegrationEventType.Cancel)
            this.error(e);
        }
      );
  }

  integrationReminded(entityId: number): void {
    const url = `${this.baseUrl}/api/banking/${entityId}/integration-reminded`;
    this.http.put<any>(url, null, { headers: { hideLoader: "true" } })
      .subscribe(_res => { /* intentionally left blank, without subscribe PUT observable does not work*/ });
  }

  private integrationSuccessful(event: IntegrationEventData): void {
  this.onSyncStarted.next(undefined);
    this.openInProgressSnackBar(event.institutionName);
    // eslint-disable-next-line max-len
    const url = `${this.baseUrl}/api/banking/${event.entityId}/integration-success`;
    this.http.put<boolean>(url, null,
      {
        params:
        {
          institutionName: event.institutionName ?? "missing-institution",
          institutionId: event.institutionId ?? "0",
          routingNumber: event.routingNumber ?? "0",
          provider: event.provider
        }
      })
      .subscribe(
        res => {
          this.onInstitutionLinkStatusChanged.next(res);
          if (res)
            this.openSuccessSnackBar(event.institutionName);
          else this.openProblemSnackBar(event.institutionName);
        },
        _err => {
          this.openFailedSnackBar(event.institutionName);
        });
  }

  private integrationInterrupted(event: IntegrationEventData): void {
    // eslint-disable-next-line max-len
    const url = `${this.baseUrl}/api/banking/${event.entityId}/integration-interrupted`;
    this.http.put(url, null,
      {
        params:
        {
          bankName: event.institutionName ?? 'missing-institution',
          institutionId: event.institutionId ?? "0",
          routingNumber: event.routingNumber ?? "0",
          info: event.info ?? "missing-info",
          provider: event.provider
        },
        headers:
        {
          hideLoader: "true"
        }
      })
      .subscribe(_res => { /* intentionally left blank, without subscribe PUT observable does not work*/ });
  }

  private integrationInprogress(event: IntegrationEventData): void {

    // eslint-disable-next-line max-len
    const url = `${this.baseUrl}/api/banking/${event.entityId}/integration-inprogress`;
    this.http.put(url, null,
      {
        params:
        {
          bankName: event.institutionName ?? "missing-bank",
          institutionId: event.institutionId ?? "0",
          routingNumber: event.routingNumber ?? "0",
          bankLinkStatusInfo: event.info ?? "missing-info",
          provider: event.provider
        },
        headers:
        {
          hideLoader: "true"
        }
      })
      .subscribe(_res => { /* intentionally left blank, without subscribe PUT observable does not work*/ });
  }

  private openInProgressSnackBar(institutionName: string) {
    this.snackBar.open(`We are currently syncing ${institutionName ? institutionName : 'a bank'}.`, 'Dismiss',
      { duration: 5000, panelClass: ['mat-white-bg', 'main-text', 'snackbar'] });
  }

  private openSuccessSnackBar(institutionName: string) {
    this.snackBar.open(`${institutionName ? institutionName : 'Your bank'} was successfully synced.`, 'Dismiss',
      { duration: 5000, panelClass: ['mat-white-bg', 'main-text', 'snackbar'] });
  }

  private openProblemSnackBar(institutionName: string) {
    this.snackBar.open(`We are currently syncing ${institutionName ? institutionName : 'your bank'} and will reach out if necessary.`, 'Dismiss',
      { duration: 5000, panelClass: ['mat-white-bg', 'main-text', 'snackbar'] });
  }

  private openFailedSnackBar(institutionName: string) {
    this.snackBar.open(`We were unable to sync ${institutionName ? institutionName : 'your bank'}.
     We are looking into this and will reach out if necessary.`, 'Dismiss',
      { duration: 5000, panelClass: ['mat-orange-bg', 'snackbar'] });
  }

  private callNextIntegration() {
    this.integrationStack.length && this.integrationStack.shift()();
  }

  relink(entityId: number, institution: FinancialInstitution, redirectPath?: string, allowLinkingAlreadyLinkedInstitution?: boolean) {
    if (!entityId)
      throw new Error('entityId argument is null');
    if (!institution)
      throw new Error('institution argument is null');

    if (!allowLinkingAlreadyLinkedInstitution && institution?.isLinked)
      return;

    this.link(entityId, redirectPath, institution);
  }

  link(entityId: number, redirectPath?: string, institution?: FinancialInstitution) {
    if (!entityId)
      throw new Error('entityId argument is null');

    const bankLinkData =
    {
      institution,
      entityId,
      redirectPath,
    }

    localStorage.setItem('bank_link_data', JSON.stringify(bankLinkData));

    combineLatest([
      this.featureFlagService.activated('Plaid'),
      this.featureFlagService.activated('DecisionLogic'),
      this.featureFlagService.activated('Finicity'),
    ]).subscribe((features) => {
      let [ plaidIsActive, decisionLogicIsActive, finicityIsActive ] = features;
      this.integrationStack.length = 0;
      if (plaidIsActive)
        this.integrationStack.push(() => this.plaidService.open(entityId, institution));
      // decisionLogic requires a routing number
      if (decisionLogicIsActive && institution?.routingNumber?.length > 0)
        this.integrationStack.push(() => this.decisionLogicService.open(entityId, institution));
      // REVISE: remove?
      if (finicityIsActive) {
        this.integrationStack.push(() => {
          if (institution)
            this.finicityService.open(entityId, institution);
          else
            this.router.navigate(['/bank-account-linking-search-institution']);
        })
      }
      if (this.integrationStack.length === 0)
        return this.messageService.error('Bank linking is temporarily unavailable, please try again later or contact your business advisor');
      this.callNextIntegration();
    });
  }

  private success(e: IntegrationEventData) {
    if (this.debug) console.debug(`${new Date().toISOString()} event:${e.type}`);
    if (e.type == IntegrationEventType.Pending)
      this.integrationInprogress(e);
    else if (e.type == IntegrationEventType.Success) {
      this.integrationSuccessful(e);
      BankAccountLinkSuccessDialogComponent.show(this.dialog, e.institutionName)
        .subscribe(res => {
          if (res) // Ok
          {
            const json = localStorage.getItem('bank_link_data');
            localStorage.removeItem('bank_link_data');
            const bankLinkData = JSON.parse(json);
            if (bankLinkData?.redirectPath)
              this.router.navigate([bankLinkData?.redirectPath]);
          }
          else // Link another bank
            this.link(e.entityId);
        });
    }
  }

  private error(err: IntegrationEventData) {
    if (this.debug) console.debug(`${new Date().toISOString()} event:${err.type}`);
    const hasNextIntegration = this.integrationStack.length > 0;

    if (err.error?.error.Message == 'Routing Number not supported.' && hasNextIntegration)
      return this.callNextIntegration();

    this.integrationInterrupted(err);

    BankAccountLinkFailedDialogComponent.show(this.dialog, err.institutionName, hasNextIntegration)
      .subscribe(res => {
        const json = localStorage.getItem('bank_link_data');
        localStorage.removeItem('bank_link_data');
        const bankLinkData = JSON.parse(json);
        if (res) //Try again
        {
          if(hasNextIntegration)
            return this.callNextIntegration();
          if (bankLinkData?.entityId && bankLinkData?.institution)
            this.relink(bankLinkData.entityId, bankLinkData.institution, bankLinkData.redirectPath);
          else
            this.link(err.entityId);
        }
        else //Link later
        {
          if (bankLinkData?.redirectPath)
            this.router.navigate([bankLinkData?.redirectPath]);
        }
      });
  }

  detectOAuthRedirect() {
    this.plaidService.detectOAuthRedirect();
  }
}
